import { useState } from 'react';
import { FiX, FiCreditCard, FiCheck, FiLoader } from 'react-icons/fi';
import { subscriptionsAPI } from '../../services/api';

const SubscriptionModal = ({ isOpen, onClose, plan, period }) => {
  const [step, setStep] = useState(1); // 1: Formulario, 2: Procesando, 3: Redirigiendo a PayPal
  const [formData, setFormData] = useState({
    establishmentName: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  // Calcular precio según el período
  const getPrice = () => {
    if (!plan) return 0;
    return period === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  };

  // Calcular descuento para período anual
  const getDiscount = () => {
    if (!plan || period !== 'yearly') return 0;
    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlyPrice = plan.yearlyPrice;
    return monthlyTotal - yearlyPrice;
  };

  // Calcular desglose de precios con IVA
  const getPriceBreakdown = () => {
    if (!plan) return { basePrice: 0, taxAmount: 0, total: 0 };

    const price = getPrice();
    const ivaPercentage = plan.ivaPercentage || 21;
    const ivaRate = ivaPercentage / 100;

    let basePrice, taxAmount, total;

    if (plan.pricesIncludeIVA) {
      // Si el precio incluye IVA, extraer el IVA del precio
      // Ej: 10.89€ → 9€ base + 1.89€ IVA
      total = price;
      basePrice = price / (1 + ivaRate);
      taxAmount = price - basePrice;
    } else {
      // Si el precio no incluye IVA, añadir el IVA
      // Ej: 9€ → 9€ base + 1.89€ IVA = 10.89€ total
      basePrice = price;
      taxAmount = price * ivaRate;
      total = price + taxAmount;
    }

    return {
      basePrice: basePrice,
      taxAmount: taxAmount,
      total: total,
      ivaPercentage: ivaPercentage
    };
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.establishmentName.trim()) {
      newErrors.establishmentName = 'El nombre del establecimiento es requerido';
    }

    if (!formData.adminName.trim()) {
      newErrors.adminName = 'El nombre del administrador es requerido';
    }

    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'El email no es válido';
    }

    if (!formData.adminPassword) {
      newErrors.adminPassword = 'La contraseña es requerida';
    } else if (formData.adminPassword.length < 8) {
      newErrors.adminPassword = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (formData.adminPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar el proceso de suscripción
  const handleSubscribe = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setStep(2);

    try {
      // Crear orden de pago en PayPal con los datos de registro
      const checkoutResponse = await subscriptionsAPI.createCheckout({
        plan: plan.plan,
        period: period === 'monthly' ? 'monthly' : 'annual',
        // Datos de registro (el backend los guardará)
        establishment_name: formData.establishmentName,
        admin_email: formData.adminEmail,
        admin_name: formData.adminName,
        admin_password: formData.adminPassword
      });

      // Guardar solo el payment_id para capturar después
      sessionStorage.setItem('pending_payment_id', checkoutResponse.payment_id);

      // Redirigir a PayPal
      setStep(3);
      window.location.href = checkoutResponse.approval_url;

    } catch (error) {
      console.error('Error creating checkout:', error);
      const errorMessage = error.response?.data?.detail || 'Error al iniciar el proceso de pago. Por favor, inténtalo de nuevo.';
      alert(errorMessage);
      setLoading(false);
      setStep(1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Suscribirse a {plan?.name}</h2>
            <p className="text-xs text-gray-600">
              {period === 'monthly' ? 'Plan Mensual' : 'Plan Anual'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Step 1: Formulario de registro */}
          {step === 1 && (
            <>
              {/* Resumen del plan */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">Resumen del Plan</h3>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Plan:</span>
                    <span className="font-semibold text-gray-900">{plan?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Período:</span>
                    <span className="font-semibold text-gray-900">
                      {period === 'monthly' ? 'Mensual' : 'Anual'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Subtotal:</span>
                    <span className="font-semibold text-gray-900">{getPriceBreakdown().basePrice.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">IVA ({getPriceBreakdown().ivaPercentage}%):</span>
                    <span className="font-semibold text-gray-900">{getPriceBreakdown().taxAmount.toFixed(2)}€</span>
                  </div>
                  {period === 'yearly' && (
                    <div className="flex justify-between text-green-600">
                      <span>Ahorro anual:</span>
                      <span className="font-semibold">{getDiscount().toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-blue-200 flex justify-between">
                    <span className="font-semibold text-gray-900">Total:</span>
                    <span className="font-bold text-blue-600 text-base">{getPriceBreakdown().total.toFixed(2)}€</span>
                  </div>
                </div>

                {/* Trial period notice */}
                <div className="mt-3 p-2.5 bg-blue-100 rounded-lg">
                  <p className="text-xs text-blue-900 flex items-start gap-2">
                    <FiCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>7 días de prueba gratis</strong> después del pago.
                      Si cancelas durante el trial, devolución del 100%.
                    </span>
                  </p>
                </div>
              </div>

              {/* Formulario de registro */}
              <form className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nombre del Establecimiento *
                    </label>
                    <input
                      type="text"
                      value={formData.establishmentName}
                      onChange={(e) => setFormData({ ...formData, establishmentName: e.target.value })}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.establishmentName ? 'border-danger-500' : 'border-gray-300'
                      }`}
                      placeholder="Hotel Ejemplo"
                    />
                    {errors.establishmentName && (
                      <p className="mt-0.5 text-xs text-danger-600">{errors.establishmentName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nombre del Administrador *
                    </label>
                    <input
                      type="text"
                      value={formData.adminName}
                      onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.adminName ? 'border-danger-500' : 'border-gray-300'
                      }`}
                      placeholder="Juan Pérez"
                    />
                    {errors.adminName && (
                      <p className="mt-0.5 text-xs text-danger-600">{errors.adminName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.adminEmail ? 'border-danger-500' : 'border-gray-300'
                    }`}
                    placeholder="admin@hotel.com"
                  />
                  {errors.adminEmail && (
                    <p className="mt-0.5 text-xs text-danger-600">{errors.adminEmail}</p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Contraseña *
                    </label>
                    <input
                      type="password"
                      value={formData.adminPassword}
                      onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.adminPassword ? 'border-danger-500' : 'border-gray-300'
                      }`}
                      placeholder="Mínimo 8 caracteres"
                    />
                    {errors.adminPassword && (
                      <p className="mt-0.5 text-xs text-danger-600">{errors.adminPassword}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Confirmar Contraseña *
                    </label>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.confirmPassword ? 'border-danger-500' : 'border-gray-300'
                      }`}
                      placeholder="Repite la contraseña"
                    />
                    {errors.confirmPassword && (
                      <p className="mt-0.5 text-xs text-danger-600">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </form>

              {/* Términos */}
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  Al continuar, aceptas nuestros términos y condiciones.
                  El pago se procesará de forma segura a través de PayPal.
                  Puedes cancelar tu suscripción en cualquier momento.
                </p>
              </div>

              {/* Botones */}
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiCreditCard className="w-4 h-4" />
                  Continuar al Pago
                </button>
              </div>
            </>
          )}

          {/* Step 2: Procesando */}
          {step === 2 && (
            <div className="py-12 text-center">
              <FiLoader className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Preparando el pago...
              </h3>
              <p className="text-gray-600">
                Estamos creando tu orden de pago segura
              </p>
            </div>
          )}

          {/* Step 3: Redirigiendo */}
          {step === 3 && (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCreditCard className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Redirigiendo a PayPal...
              </h3>
              <p className="text-gray-600 mb-4">
                Serás redirigido a PayPal para completar el pago de forma segura
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <FiLoader className="w-4 h-4 animate-spin" />
                <span>Por favor espera...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
